/**
 * A DICA DE INSTALAR, na tela de entrar (pedido de produto de 19/10/2026).
 *
 * Os pastores perguntaram se o painel vai ser "app" no celular. Vai, sem loja: ele se instala pela
 * própria página. Esta dica diz como, com o passo do sistema do aparelho.
 *
 * Ela aparece **só no celular, e só enquanto o painel não foi instalado**. Aberto pelo ícone da
 * tela inicial, o painel roda em tela cheia (`display-mode: standalone`, ou `navigator.standalone`
 * no iPhone), e a dica some. No computador ela não aparece: lá a pergunta não foi feita.
 *
 * No Android, quando o Chrome se oferece para instalar, a dica ganha o botão que instala direto.
 * O aviso do Chrome é pego aqui, no carregar da página, porque ele pode chegar antes desta tela.
 */
import { useEffect, useState, type JSX } from 'react'

type Sistema = 'android' | 'iphone' | 'outro'

/** O aviso do Chrome de que dá para instalar. Não tem tipo no TypeScript do navegador. */
interface PedidoDeInstalar extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let pedidoGuardado: PedidoDeInstalar | null = null
const quemQuerSaber = new Set<() => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (evento) => {
    // Sem isto o Chrome mostra a faixa dele por cima da tela de entrar; o botão daqui faz o mesmo.
    evento.preventDefault()
    pedidoGuardado = evento as PedidoDeInstalar
    quemQuerSaber.forEach((avisar) => avisar())
  })
  window.addEventListener('appinstalled', () => {
    pedidoGuardado = null
    quemQuerSaber.forEach((avisar) => avisar())
  })
}

/** Qual celular é, ou nulo quando não é celular. */
function oSistema(): Sistema | null {
  const ua = navigator.userAgent
  if (/iPhone|iPod/i.test(ua)) return 'iphone'
  if (/Android/i.test(ua)) return 'android'
  if (/Mobile/i.test(ua)) return 'outro'
  return null
}

/** Aberto pelo ícone da tela inicial, já instalado. */
function jaInstalado(): boolean {
  const naTelaCheia =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  return naTelaCheia || (navigator as { standalone?: boolean }).standalone === true
}

export function AdicionarATelaInicial(): JSX.Element | null {
  const [, redesenhar] = useState(0)

  useEffect(() => {
    const avisar = (): void => redesenhar((n) => n + 1)
    quemQuerSaber.add(avisar)
    return () => {
      quemQuerSaber.delete(avisar)
    }
  }, [])

  const sistema = oSistema()
  if (sistema === null || jaInstalado()) return null

  async function instalar(): Promise<void> {
    const pedido = pedidoGuardado
    if (!pedido) return
    await pedido.prompt()
    await pedido.userChoice.catch(() => undefined)
    pedidoGuardado = null
    redesenhar((n) => n + 1)
  }

  return (
    <section className="dica-do-aplicativo so-tela" aria-label="Instalar no celular">
      <p className="titulo">Adicione à tela inicial para abrir como aplicativo</p>

      {sistema !== 'iphone' && (
        <p>
          <strong>Android:</strong> no Chrome, toque nos três pontinhos <span aria-hidden="true">⋮</span> no
          alto da tela e depois em <strong>Instalar aplicativo</strong> (em alguns celulares,
          <strong> Adicionar à tela inicial</strong>).
        </p>
      )}

      {sistema !== 'android' && (
        <p>
          <strong>iPhone:</strong> no Safari, toque em <strong>Compartilhar</strong> (o quadrado com a
          seta para cima, na barra de baixo) e depois em <strong>Adicionar à Tela de Início</strong>.
        </p>
      )}

      {sistema === 'android' && pedidoGuardado !== null && (
        <button type="button" className="secundario" onClick={() => void instalar()}>
          Instalar aplicativo
        </button>
      )}

      <p className="miudo">
        Pelo ícone, o painel abre em tela cheia. Depois de entrar por ele, o celular fica lembrado
        por 30 dias desde o último uso.
      </p>
    </section>
  )
}
